"use client";
import React, { useState } from "react";
import { notFound } from "next/navigation";
import { getUseCaseBySlug } from "@/data/useCases";
import { UseCaseData } from "@/types/global";

// Components
import Header from "@/components/common/header";
import Footer from "@/components/common/footer";
import PreFooter from "@/components/common/preFooter";
import WhoThisIsFor from "@/components/pages/use-case/who-this-is-for";
import ServiceAreaOther from "@/components/pages/icp/service-area-other";
import Outcomes from "@/components/pages/use-case/outcomes";
import FaqArea from "@/components/common/faq-area";
import FormModalClient from "@/components/common/form-modal-client";
import FormModal from "@/components/common/form-modal";
import Hero2 from "@/components/common/hero2";
import WhyItMatter from "@/components/pages/use-case/why-it-matter";
import Expertise from "@/components/pages/icp/expertise";
import { ServiceArea } from "@/components/common";
import { useModal } from "@/hooks/useModal";
import SecurityGovernance from "./security-governance";

interface UseCasePageClientProps {
    slug: string;
}

const UseCasePageClient = ({ slug }: UseCasePageClientProps) => {
    const [useCase, setUseCase] = useState<UseCaseData | null>(null);
    const modal = useModal();
    const [modalTitle, setModalTitle] = useState("Get Started");

    // Load use case data
    React.useEffect(() => {
        const resolvedUseCase = getUseCaseBySlug(slug);

        if (!resolvedUseCase) {
            notFound();
        }

        setUseCase(resolvedUseCase);
    }, [slug]);

    const handleModalOpen = (modalTitle?: string) => {
        if (modalTitle) {
            setModalTitle(modalTitle);
        }
        modal.openModal();
    };

    if (!useCase) {
        return null;
    }

    return (
        <>
            <Header />
            <Hero2
                title={useCase.hero.title}
                description={useCase.hero.description}
                btn={useCase.hero.cta.primary.text}
                btn1={useCase.hero.cta.secondary.text}
            />
            <WhoThisIsFor
                title={useCase.whoThisIsFor.title}
                description={useCase.whoThisIsFor.description}
            />
            <ServiceAreaOther
                title={useCase.whatYouCanLaunch.title}
                description={useCase.whatYouCanLaunch.description}
                data={useCase.whatYouCanLaunch.items}
            />
            <WhyItMatter
                title={useCase.whyItMatters.title}
                description={useCase.whyItMatters.description}
                data={useCase.whyItMatters.items}
            />
            {/* <HowItWorks
                title={useCase.howItWorks.title}
                description={useCase.howItWorks.description}
                steps={useCase.howItWorks.steps}
            /> */}
            <Expertise
                title={useCase.howItWorks.title}
                description={useCase.howItWorks.description}
                data={useCase.howItWorks.steps}
            />
            <Outcomes
                title={useCase.outcomes.title}
                description={useCase.outcomes.description}
                items={useCase.outcomes.items}
            />
            <ServiceArea
                title={useCase.technicalApproach.title}
                description={useCase.technicalApproach.description}
                data={useCase.technicalApproach.items}
            />
            <WhyItMatter
                title={useCase.commonAddOns.title}
                description={useCase.commonAddOns.description}
                data={useCase.commonAddOns.items}
            />
            <SecurityGovernance
                title={useCase.securityAndGovernance.title}
                description={useCase.securityAndGovernance.description}
                data={useCase.securityAndGovernance}
            />
            <FaqArea
                title={useCase.faqs.title}
                items={useCase.faqs.items}
                style="style-1"
            />
            <PreFooter
                title="Ready to turn vision into action?"
                description="Collaborate with our experts to define clear goals and build actionable strategies for success."
                onModalOpen={handleModalOpen}
                cta={useCase.preFooter?.cta}
            />
            <Footer />
            <FormModalClient
                defaultTitle={useCase.hero.cta.secondary.modalTitle}
                defaultSlug={useCase.hero.cta.secondary.modalTitle}
            />

            {/* Form Modal */}
            <FormModal
                isOpen={modal.isOpen}
                onClose={modal.closeModal}
                onSubmit={() => {
                    modal.closeModal();
                }}
                title={modalTitle}
                submitButtonText="Get Started"
                dialogSlug={modalTitle}
            />
        </>
    );
};

export default UseCasePageClient;
